import { useState, useEffect, useMemo } from "react";
import { useParams } from "@tanstack/react-router";
import { contentfulClient } from "../services/api.ts";
import { GetPageBySlug } from "../queries/GetPageBySlug.ts";
import { useGlobalContext } from "../utils/GlobalContext.ts";
import {
  componentTransformer,
  TransformedComponentProps,
} from "../data/transformer/componentTransformer.ts";

type MetaData = {
  title?: string;
  description?: string;
  image?: string;
};

interface PageData {
  pageData: {
    componentContent: TransformedComponentProps | null;
    metaData: MetaData;
  };
  loading: boolean;
  notFound: boolean;
  currentSlug: string;
  currentLocale: string | null;
}

export function usePageData(): PageData {
  const params = useParams({ strict: false });
  const { localeConfig, previewContent } = useGlobalContext();
  const [componentContent, setComponentContent] =
    useState<TransformedComponentProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [metaData, setMetaData] = useState<MetaData>({});
  const [notFound, setNotFound] = useState(false);

  // Get the current slug from params, default to "/" for index route
  const currentSlug = useMemo(() => params.slug || "/", [params.slug]);

  useEffect(() => {
    if (!localeConfig.currentLocale) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    async function fetchPageData() {
      try {
        setLoading(true);
        setNotFound(false);

        const pageResponse = await contentfulClient.query(GetPageBySlug, {
          slug: currentSlug,
          locale: localeConfig.currentLocale ?? undefined,
        });

        const page = pageResponse.pageCollection?.items[0];

        if (!page) {
          setNotFound(true);
          setComponentContent(null);
          return;
        }

        const pageContent = await componentTransformer(page, {
          localeConfig,
          previewContent,
        });

        if (pageContent.content.length === 0) {
          setNotFound(true);
          setComponentContent(null);
          return;
        }

        setMetaData({
          title: page.metadata?.pageTitle ?? undefined,
          description: page.metadata?.description ?? undefined,
          image: page.metadata?.ogImage?.url ?? undefined,
        });
        setComponentContent(pageContent);
      } catch (error) {
        console.error("Error fetching page data:", error);
        setNotFound(true);
        setComponentContent(null);
      } finally {
        setLoading(false);
      }
    }

    fetchPageData();
  }, [currentSlug, localeConfig, localeConfig.currentLocale, previewContent]);

  return {
    pageData: {
      componentContent,
      metaData,
    },
    loading,
    notFound,
    currentSlug,
    currentLocale: localeConfig.currentLocale,
  };
}
